import { useToast } from '@ais-ui/ui-core';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  getEmptySessions,
  getEmptyWebsockets,
  initializeWebsockets,
  Sessions,
  User,
  ValidatedSessions,
  WebSockets,
  createClientWithCredentials,
  createSession,
} from './createSession';
import {
  clearCredentials,
  hasStoredCredentials,
  getSavedCredentials,
  saveCredentials,
} from './tokenStorage';
import { useQueryClient } from '@tanstack/react-query';
import { useProfile } from '@ais-ui/profile';
import { msalInstance } from './config/authConfig';
import { useReconnect } from './useReconnect';
import { AuthenticationResult } from '@azure/msal-browser/src/response/AuthenticationResult';
import { ROUTES } from '../../../../apps/ais/src/routes';
import { useBasePath } from '@ais-ui/custom-hooks';
import { checkSessionValid, getOverlayMP } from '@ais-ui/websockets';

export interface UserCredentials {
  username: string;
  password: string;
}

type AuthMethod = 'credentials' | 'azure';

interface AuthProviderContextState {
  credentials?: UserCredentials;
  sessions: Sessions;
  authMethod?: AuthMethod;
  login: (sessions: ValidatedSessions, user: User) => void;
  loginWithAzure: () => Promise<AuthenticationResult | undefined>;
  logout: (reason: string) => void;
  reconnect: (server: string) => void;
  storeCredentials: (credentials: UserCredentials) => void;
  twoFactorEnabled: boolean;
  setTwoFactorEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  initiateWebsockets: () => Promise<WebSockets>;
  setSessions: React.Dispatch<React.SetStateAction<Sessions>>;
  attemptAutoLogin: () => Promise<boolean>;
  isInitializing: boolean;
}

const AuthContext = createContext<AuthProviderContextState | undefined>(
  undefined,
);

export interface AuthProviderProps {
  children: ReactNode;
  onUserLogout?: () => void;
  initialCredentials?: UserCredentials;
}

export const AuthProvider = ({
  children,
  initialCredentials,
}: AuthProviderProps) => {
  const { hostname } = useBasePath();
  const [credentials, setCredentials] = useState<UserCredentials | undefined>(
    initialCredentials,
  );
  const [authMethod, setAuthMethod] = useState<AuthMethod>('credentials');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    if (!msalInstance) {
      return;
    }
    msalInstance.initialize().catch((error) => {
      console.error('Failed to initialize MSAL:', error);
      Sentry.captureException(error);
    });
  }, []);

  const { setEmail, setUsername, setGroups, setUserId } = useProfile();
  const [webSockets, setWebSockets] =
    useState<WebSockets>(getEmptyWebsockets());
  const [sessions, setSessions] = useState<Sessions>(getEmptySessions());
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const storeCredentials = useCallback(
    (credentials: UserCredentials) => {
      setCredentials(credentials);
    },
    [setCredentials],
  );

  const logout = useCallback(
    (reason = '') => {
      if (authMethod === 'azure' && msalInstance) {
        msalInstance.logoutPopup().catch(console.error);
        msalInstance.clearCache();
      }
      setCredentials(undefined);
      setSessions(getEmptySessions());
      setGroups([]);
      queryClient.clear();
      Sentry.setUser(null);

      // Clear saved credentials and sessionId
      clearCredentials();

      // Store logout reason for login form to display
      if (typeof reason === 'string' && reason) {
        sessionStorage.setItem('logoutReason', reason);
      }

      navigate('/'); // Navigate to login page after logout

      if (typeof reason === 'string' && reason) {
        toast({
          className: 'logout-success-toast',
          title: 'Logged out',
          description: reason,
        });
      }
    },
    [authMethod, navigate, queryClient, setGroups, toast],
  );

  const reconnect = useReconnect(sessions, setSessions, toast, logout);

  const login = useCallback(
    (sessions: ValidatedSessions, user: User) => {
      setSessions(sessions);

      setGroups(user.groups);
      setEmail(user.email);
      setUsername(user.username);
      setUserId(user.userId);

      // Store username and sessionId
      // On localhost, also preserve password for easier re-authentication
      if (sessions?.sessionId) {
        const isLocalhost = window.location.hostname.includes('localhost');
        const existingCredentials = getSavedCredentials();

        // On localhost, use password from credentials state (set by storeCredentials before login)
        // or fall back to existing localStorage password
        const passwordToSave =
          isLocalhost
            ? credentials?.password || existingCredentials?.password
            : undefined;

        saveCredentials({
          username: user.username,
          sessionId: sessions.sessionId,
          password: passwordToSave,
        });

        // Also update credentials state with password on localhost
        if (isLocalhost && passwordToSave) {
          setCredentials({
            username: user.username,
            password: passwordToSave,
          });
        }
      }

      Sentry.setUser({
        id: user.userId,
        username: user.username,
        email: user.email,
        groups: user.groups,
        session_id: sessions?.sessionId,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      });
      navigate(ROUTES.MAP.$path());
    },
    [credentials, navigate, setEmail, setGroups, setUserId, setUsername],
  );

  const loginWithAzure = useCallback(async () => {
    try {
      if (!msalInstance) {
        throw new Error('MSAL not initialized');
      }

      const response = await msalInstance.loginPopup({
        scopes: ['https://graph.microsoft.com/.default'],
      });
      setAuthMethod('azure');
      // Clear any stored credentials from previous credential-based logins
      clearCredentials();

      // Note: sessionId will be stored when login() is called after Azure authentication completes

      return response;
    } catch (error) {
      console.error('Azure login failed:', error);
      toast({
        title: 'Login Failed',
        description: 'Failed to login with Azure',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const initiateWebsockets = useCallback(async (): Promise<WebSockets> => {
    if (webSockets) {
      const webSocketProperties = [
        'olWebsocket',
        'dbWebsocket',
        'replayWebsocket',
      ] as const;

      for (const prop of webSocketProperties) {
        const socket = webSockets[prop];
        if (socket && socket.readyState !== WebSocket.CLOSED) {
          socket.close(1000, 'Reinitializing connections');
        }
      }
    }

    const initiatedWebsockets = await initializeWebsockets();

    setWebSockets(initiatedWebsockets);

    return initiatedWebsockets;
  }, [webSockets]);

  const tryRestoreSessionById = useCallback(
    async (
      savedCredentials: NonNullable<ReturnType<typeof getSavedCredentials>>,
    ): Promise<boolean> => {
      if (!savedCredentials.sessionId) {
        return false;
      }

      try {
        const initiatedWebsockets = await initializeWebsockets();
        setWebSockets(initiatedWebsockets);

        const { olSession, dbSession, replaySession } = createSessionsWithId(
          initiatedWebsockets,
          savedCredentials.sessionId,
        );

        if (!olSession) {
          return false;
        }

        const isValid = await checkSessionValid(
          olSession,
          savedCredentials.sessionId,
          savedCredentials.username,
        );

        if (!isValid) {
          return false;
        }

        // Initialize the mapview on the backend by calling getOverlayMP with minimal params.
        // This is required because the backend needs a mapview before settings can be fetched.
        // Using a tiny area (1x1 pixel, high zoom) to minimize backend work.
        await getOverlayMP(
          olSession,
          {
            centerLat: 0.0001,
            centerLon: 0.0001,
            zoomLevel: 18,
            width: 1,
            height: 1,
            leftLon: 0.0001,
            rightLon: 0.0002,
          },
          'UTC',
        );

        const restoredSessions: ValidatedSessions = {
          olSession,
          dbSession,
          replaySession,
          sessionId: savedCredentials.sessionId,
        };
        setSessions(restoredSessions);

        // On localhost, restore credentials state with password for API auth headers
        const isLocalhost = window.location.hostname.includes('localhost');
        if (isLocalhost && savedCredentials.password) {
          setCredentials({
            username: savedCredentials.username,
            password: savedCredentials.password,
          });
        }

        restoreUserSession(
          savedCredentials.username,
          savedCredentials.sessionId,
          setUsername,
          navigate,
        );

        return true;
      } catch (error) {
        console.error('Error checking session validity:', error);
        return false;
      }
    },
    [navigate, setUsername],
  );

  const tryLoginWithPassword = useCallback(
    async (
      savedCredentials: NonNullable<ReturnType<typeof getSavedCredentials>>,
    ): Promise<boolean> => {
      if (!savedCredentials.password) {
        return false;
      }

      const isLocalhost = window.location.hostname.includes('localhost');
      if (!isLocalhost) {
        console.log('Password-based login is only available on localhost');
        clearCredentials();
        return false;
      }

      try {
        const initiatedWebsockets = await initializeWebsockets();
        setWebSockets(initiatedWebsockets);

        const result = await createClientWithCredentials(
          {
            username: savedCredentials.username,
            password: savedCredentials.password,
          },
          initiatedWebsockets,
        );

        if (result.sessions.sessionId && result.user.userId) {
          setCredentials({
            username: savedCredentials.username,
            password: savedCredentials.password,
          });

          login(result.sessions as ValidatedSessions, result.user);
          return true;
        } else {
          clearCredentials();
          return false;
        }
      } catch (error) {
        console.error('Password-based auto-login failed:', error);
        clearCredentials();
        return false;
      }
    },
    [login],
  );

  const attemptAutoLogin = useCallback(async (): Promise<boolean> => {
    if (!hasStoredCredentials()) {
      return false;
    }

    const savedCredentials = getSavedCredentials();
    if (!savedCredentials) {
      return false;
    }

    const sessionRestored = await tryRestoreSessionById(savedCredentials);
    if (sessionRestored) {
      return true;
    }

    const passwordLoginSuccessful =
      await tryLoginWithPassword(savedCredentials);
    if (passwordLoginSuccessful) {
      return true;
    }

    return false;
  }, [tryRestoreSessionById, tryLoginWithPassword]);

  // Attempt automatic login on mount if there are saved credentials
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // If we already have sessions, no need to auto-login
      if (sessions.olSession) {
        setIsInitializing(false);
        return;
      }

      // Try to auto-login with saved credentials
      const loggedIn = await attemptAutoLogin();

      if (mounted) {
        setIsInitializing(false);
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []); // Only run on mount

  return (
    <AuthContext.Provider
      value={{
        credentials,
        sessions,
        reconnect,
        login,
        loginWithAzure,
        logout,
        authMethod,
        storeCredentials,
        twoFactorEnabled,
        setTwoFactorEnabled,
        initiateWebsockets,
        setSessions,
        attemptAutoLogin,
        isInitializing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return authContext;
};

const createSessionsWithId = (
  initiatedWebsockets: WebSockets,
  sessionId: string,
) => {
  const olSession = createSession(
    initiatedWebsockets.olServerAndClient,
    initiatedWebsockets.olWebsocket,
    sessionId,
    'olSession',
  );
  const dbSession = createSession(
    initiatedWebsockets.dbServerAndClient,
    initiatedWebsockets.dbWebsocket,
    sessionId,
    'dbSession',
  );
  const replaySession = createSession(
    initiatedWebsockets.replayServerAndClient,
    initiatedWebsockets.replayWebsocket,
    sessionId,
    'replaySession',
  );

  return { olSession, dbSession, replaySession };
};

const restoreUserSession = (
  username: string,
  sessionId: string,
  setUsername: (username: string) => void,
  navigate: (path: string) => void,
) => {
  setUsername(username);

  Sentry.setUser({
    id: '',
    username: username,
    session_id: sessionId,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
  });

  navigate(ROUTES.MAP.$path());
};

export const useCredentials = () => {
  const authContext = useAuth();

  const { credentials } = authContext;

  // if (credentials == null) {
  //   throw new Error('useCredentials must be used after user is logged in');
  // }

  return credentials;
};
